import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { HttpMethod } from "./common";
import type { EpisodeFileFormat } from "./datasetEpisode";
import type { PageQuery, PageResp } from "./page";

// Dataset(primary anchor source)의 schema 밖(extra) 파일 read API 다. backend
// api/v1/dataset/extra_files 가 제공한다. 서버는 hatchery-data FilePattern 에 맞지 않는 파일을
// 메타데이터로 반환한다(예: root 직속 instruction.json, annotation/** 서브트리).
// 다운로드 URL 은 단건 endpoint 로 받거나, 목록에서 with_download_url 로 인라인 받는다.
// read 요청에는 org-actor visibility(404 gate)만 적용한다.

// 파일 1건이다. backend ExtraArtifactFileResp 와 1:1 이다.
export type ExtraArtifactFile = {
  uuid: string;
  relative_path: string;
  // source root 기준 부모 폴더 경로다(루트 직속이면 ''). 검색·목록에서 위치를 표시할 때 쓴다.
  parent_path: string;
  file_format: EpisodeFileFormat;
  size_bytes: number | null;
  content_hash: string | null;
  created_at: string;
  modified_at: string;
  // 인라인 signed GET URL 이다. 키는 항상 있다. 목록을 with_download_url=true 로 호출하지
  // 않았으면 null 이다. 여러 파일을 한 번에 다운로드할 때 단건 발급 왕복을 없애려고 쓴다.
  download_url: string | null;
  download_url_expires_at: string | null;
};

// 1회용 signed GET URL 이다. backend ExtraArtifactFileUrlResp 와 1:1 이다. client 가 url 로
// 직접 GET 한다. method 는 항상 GET 이다. expires_at 이 지나 403 을 받으면 이 endpoint 를 다시 호출한다.
export type ExtraArtifactFileUrl = {
  url: string;
  method: HttpMethod;
  expires_at: string;
};

// 목록 필터:
//   - parent 를 지정하지 않으면 source 전체(전역)
//   - parent='annotation/cam0' 이고 recursive=false(기본)면 직속만, recursive=true 면 하위까지
//   - parent='' 는 루트 직속
//   - search 는 relative_path 부분 일치, file_format 은 포맷 필터
//   - with_download_url=true 면 서버가 행마다 signed GET 을 인라인한다
// 서명에는 비용이 든다. 그래서 with_download_url 은 실제로 다운로드할 때만 켠다.
export type ExtraArtifactFileListQuery = PageQuery & {
  parent?: string;
  recursive?: boolean;
  search?: string;
  file_format?: EpisodeFileFormat;
  with_download_url?: boolean;
};

// ── write(member+) ────────────────────────────────────────────────────────
// 업로드는 3단계다: declare → client 가 signed URL 로 직접 PUT → finalize.
// registry 는 바이트를 프록시하지 않는다. rename·delete 는 단건 경로를 쓴다.

// 업로드 선언 1건이다. relative_path는 번들 안에서 유일하다. 포맷·크기·해시는 서버가 계산한다.
export type ExtraFileDeclareItem = {
  relative_path: string;
};

export type ExtraFilesDeclareReq = {
  files: ExtraFileDeclareItem[];
};

// 선언한 파일 1건의 1회용 signed PUT URL 이다. 서버는 등록 uuid 를 finalize 후에 부여한다.
// 그래서 여기에는 uuid 가 없다.
export type ExtraFileUploadTicket = {
  relative_path: string;
  upload_url: string;
  method: HttpMethod;
  expires_at: string;
  // PUT 에 그대로 담아야 하는 헤더다. 값은 Dataset 의 저장 위치마다 다르다.
  // - GCS: 새 경로면 `x-goog-if-generation-match: 0` 이 들어 있다. 이 헤더는 서명에 포함된다. storage 가
  //   기존 object 를 덮어쓰지 않고 412 로 거절한다. replace 티켓에서는 비어 있다.
  // - Azure: 항상 `x-ms-blob-type: BlockBlob` 이 들어 있다. 새 경로의 티켓은 create 권한뿐이라 기존
  //   object 가 있으면 403 이다. PUT 한 번은 5,000 MiB 까지다.
  // 구 BE 응답에는 이 키가 없어서 optional 이다. BE 가 배포되면 필수로 바꾼다.
  // @compat ZERG-573 until=be:293bc284
  headers?: Record<string, string>;
  // 같은 경로에 extra 파일이 이미 있어서 이번 업로드가 그 내용을 바꾸는지 나타낸다.
  // 위 필드와 같은 이유로 배포 기간 동안 optional 이다.
  // @compat ZERG-573 until=be:293bc284
  replaces?: boolean;
};

export type ExtraFilesDeclareResp = {
  files: ExtraFileUploadTicket[];
};

// PUT 이 성공한 relative_path 만 등록한다(부분 실패 방어). 경로가 빠졌거나 object 가 없으면
// 서버는 422 incomplete_upload 를 반환한다.
export type ExtraFilesFinalizeReq = {
  relative_paths: string[];
};

// rename payload 다. 경로나 파일명을 바꾼다. 충돌하면 409 duplicate_path 를 받는다.
export type ExtraFileRenameReq = {
  relative_path: string;
};

const EXTRA_FILE_RELATIVE_PATH_MAX_LENGTH = 255;

function assertExtraFileRelativePathLength(relativePath: string): void {
  if (Array.from(relativePath).length <= EXTRA_FILE_RELATIVE_PATH_MAX_LENGTH) {
    return;
  }
  throw new RangeError("추가 파일 경로는 폴더를 포함해 255자 이하여야 합니다.");
}

// extra 파일 page 를 조회한다(relative_path 순). 파일이 없으면 빈 page 를 반환한다(404 아님).
// 404 dataset.not_found 는 dataset 이 없거나 visibility 조건을 충족하지 않는다는 뜻이다.
export async function listDatasetExtraFiles(
  datasetUuid: string,
  query: ExtraArtifactFileListQuery = {},
): Promise<PageResp<ExtraArtifactFile>> {
  const response = await apiClient.get<PageResp<ExtraArtifactFile>>(
    apiRoutes.dataset.extraFiles(datasetUuid),
    { params: query },
  );
  return response.data;
}

// 단건 파일의 1회용 signed GET URL 을 발급한다(다운로드). 404 dataset.not_found / 404
// extra_file.not_found. 파일이 이 dataset 소속이 아니어도 extra_file.not_found 를 반환한다.
// 그래서 다른 dataset 의 파일 존재가 노출되지 않는다.
export async function getDatasetExtraFileUrl(
  datasetUuid: string,
  extraFileUuid: string,
): Promise<ExtraArtifactFileUrl> {
  const response = await apiClient.get<ExtraArtifactFileUrl>(
    apiRoutes.dataset.extraFileUrl(datasetUuid, extraFileUuid),
  );
  return response.data;
}

// canonical meta 파일(meta/info.json 등) page 를 조회한다. 계약은 extra-files 와 같다(응답
// shape·필터 동일). schema 가 claim 하는 파일이라서 endpoint 가 따로 있다(/meta-files).
// 파일이 없으면 빈 page 를 반환한다(404 아님).
export async function listDatasetMetaFiles(
  datasetUuid: string,
  query: ExtraArtifactFileListQuery = {},
): Promise<PageResp<ExtraArtifactFile>> {
  const response = await apiClient.get<PageResp<ExtraArtifactFile>>(
    apiRoutes.dataset.metaFiles(datasetUuid),
    { params: query },
  );
  return response.data;
}

// 단건 meta 파일의 1회용 signed GET URL 을 발급한다. 404 dataset.not_found / 404 meta_file.not_found.
export async function getDatasetMetaFileUrl(
  datasetUuid: string,
  metaFileUuid: string,
): Promise<ExtraArtifactFileUrl> {
  const response = await apiClient.get<ExtraArtifactFileUrl>(
    apiRoutes.dataset.metaFileUrl(datasetUuid, metaFileUuid),
  );
  return response.data;
}

// 업로드할 파일 목록을 선언하고(member+) 파일별 1회용 signed PUT URL 을 받는다.
// 409 duplicate_path / 422 invalid_path. 권한 등급은 서버가 강제한다. FileBrowser 는 UI 에서
// 먼저 막는다.
export async function declareDatasetExtraFiles(
  datasetUuid: string,
  payload: ExtraFilesDeclareReq,
): Promise<ExtraFilesDeclareResp> {
  payload.files.forEach((file) => assertExtraFileRelativePathLength(file.relative_path));
  const response = await apiClient.post<ExtraFilesDeclareResp>(
    apiRoutes.dataset.extraFiles(datasetUuid),
    payload,
  );
  return response.data;
}

// 서버가 업로드를 검증(HEAD)하고 등록을 확정한다(member+). PUT 이 성공한 relative_path 만
// 전달한다. 등록된 파일을 반환한다.
export async function finalizeDatasetExtraFiles(
  datasetUuid: string,
  payload: ExtraFilesFinalizeReq,
): Promise<ExtraArtifactFile[]> {
  payload.relative_paths.forEach(assertExtraFileRelativePathLength);
  const response = await apiClient.post<ExtraArtifactFile[]>(
    apiRoutes.dataset.extraFilesFinalize(datasetUuid),
    payload,
  );
  return response.data;
}

// rename(member+)이다. relative_path 를 바꾼다. 404 not_found / 409 duplicate_path.
// 갱신된 파일을 반환한다.
export async function renameDatasetExtraFile(
  datasetUuid: string,
  extraFileUuid: string,
  payload: ExtraFileRenameReq,
): Promise<ExtraArtifactFile> {
  assertExtraFileRelativePathLength(payload.relative_path);
  const response = await apiClient.patch<ExtraArtifactFile>(
    apiRoutes.dataset.extraFileDetail(datasetUuid, extraFileUuid),
    payload,
  );
  return response.data;
}

// 삭제한다(member+·204).
export async function deleteDatasetExtraFile(
  datasetUuid: string,
  extraFileUuid: string,
): Promise<void> {
  await apiClient.delete(apiRoutes.dataset.extraFileDetail(datasetUuid, extraFileUuid));
}
